import { useState } from "react";

import { Button } from "@/components/ui";

import { useCommunication } from "@/contexts/chat-provider";
import { useUser } from "@/contexts/user-provider";

import { parseError } from "@/lib/parse-error";
import { PERMISSIONS, hasAnyPermission } from "@/lib/permissions";
import { toast } from "@/lib/use-toast";

import { UserType } from "@/types/enums";
import { EventStatus } from "@/types/enums/event";
import { MessageKind } from "@/types/enums/message";
import { EventTiny } from "@/types/event";

import { useGenerateGoogleMeetLinkMutation } from "@/query/check-in/calendar/event-query";
import { useMessageDraftStore } from "@/stores/message-draft-store";

interface AppointmentMeetingLinkButtonProps extends React.ComponentProps<
  typeof Button
> {
  appointment: EventTiny;
}

export function useAppointmentMeetingLinkAction(appointment: EventTiny) {
  const { currentUser } = useUser();
  const { openChat } = useCommunication();
  const { handleDraftChange } = useMessageDraftStore();
  const generateMeetingLink = useGenerateGoogleMeetLinkMutation();
  const [isOpeningChat, setIsOpeningChat] = useState(false);

  const patient = appointment.attendees.find(
    (attendee) => attendee.user.type === UserType.PATIENT,
  )?.user;
  const canShareMeetingLink =
    !!patient &&
    ![
      EventStatus.CANCELLED,
      EventStatus.COMPLETED,
      EventStatus.NO_SHOW,
    ].includes(appointment.status) &&
    hasAnyPermission(currentUser, [
      PERMISSIONS.SCHEDULING_APPOINTMENTS_WRITE,
    ]) &&
    hasAnyPermission(currentUser, [PERMISSIONS.MESSAGING_CHATS_WRITE]);

  const handleShareMeetingLink = async () => {
    if (!patient || !canShareMeetingLink) return;
    setIsOpeningChat(true);
    try {
      const event = await generateMeetingLink.mutateAsync({
        eventId: appointment.id,
      });
      if (!event?.meetingLink) {
        throw new Error("Google Calendar did not return a meeting link.");
      }

      let chatOpened = false;
      await openChat(patient.id, undefined, (chatId) => {
        handleDraftChange(
          chatId,
          `Here is your Google Meet link for your appointment: ${event.meetingLink}`,
          { kind: MessageKind.EVENT, eventId: event.id },
        );
        chatOpened = true;
      });

      if (chatOpened) {
        toast({
          title: "Google Meet link ready",
          description: "Review and send it in the patient chat.",
          variant: "success",
        });
      }
    } catch (error) {
      toast({
        title: "Could not prepare Google Meet link",
        description: await parseError(error),
        variant: "error",
      });
    } finally {
      setIsOpeningChat(false);
    }
  };

  return {
    canShareMeetingLink,
    handleShareMeetingLink,
    isPending: generateMeetingLink.isPending || isOpeningChat,
  };
}

export function AppointmentMeetingLinkButton({
  appointment,
  ...props
}: AppointmentMeetingLinkButtonProps) {
  const { canShareMeetingLink, handleShareMeetingLink, isPending } =
    useAppointmentMeetingLinkAction(appointment);

  if (!canShareMeetingLink) return null;

  return (
    <Button
      variant="secondary"
      type="button"
      onClick={handleShareMeetingLink}
      disabled={isPending}
      {...props}
    >
      <span>{isPending ? "Generating…" : "Send Telehealth link"}</span>
    </Button>
  );
}
